/* eslint-disable react-hooks/exhaustive-deps */
/**
 * StudentPageV2.jsx — auto-ported V2 of StudentPage.jsx.
 *
 * Backend: uniapp-backend (`staging-api.uniapp.vn`) via datV2.
 * Fields: camelCase (sessionId / startTime / vehiclePlate / etc).
 * Response: { data, pagination } envelope (auto-unwrapped by uniappClient).
 *
 * See docs/v2-migration-from-n8n.md for the full migration guide.
 */
import { AgGridReact } from 'ag-grid-react';
import V2PageLayout from '../../components/v2/V2PageLayout';
import CumHangDoi from '../../components/v2/CumHangDoi';
import OQuangDuong from '../../components/v2/OQuangDuong';
import { catHaiChuSo } from '../../utils/dinhDangSo';
import {
  HANG_DOI,
  docHangDoiTuUrl,
  hangDoiBoQuaTen,
  truyVanCuaHangDoi,
} from '../../utils/hangDoiHocVien';
import { useEffect, useRef, useState } from 'react';
import datV2 from '../../services/datV2';
import { Badge, Button, Col, Dropdown, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ROLE } from '../../constants/roles.constant';
import CopyableText from '../../components/CopyableText';

// Chỉ người có quyền duyệt (DAT_APPROVER 17, hoặc admin 1/10) mới thấy nút
// cập nhật kết quả DAT lên GetFly — khớp BE guard requireDatApprover.
// DAT_ADMIN (15) thường vẫn dùng trang nhưng KHÔNG thấy nút (BE cũng 403).
// Kiểm tra từ localStorage `user-info` (giống AdminGuard).
function canApproveDatResult() {
  try {
    const user = JSON.parse(localStorage.getItem('user-info') || 'null');
    const roles = Array.isArray(user?.role) ? user.role : [];
    return (
      roles.includes(ROLE.DRIVING.DAT_APPROVER) ||
      roles.includes(ROLE.DRIVING.ADMIN) ||
      roles.includes(ROLE.ADMIN)
    );
  } catch (_) {
    return false;
  }
}

// Người CHECK DAT (DAT_ADMIN) — được gửi phê duyệt. Approver/admin cũng có
// quyền check nên cũng thấy nút gửi. Khớp BE guard requireDatAdmin.
function canCheckDat() {
  try {
    const user = JSON.parse(localStorage.getItem('user-info') || 'null');
    const roles = Array.isArray(user?.role) ? user.role : [];
    return (
      roles.includes(ROLE.DRIVING.DAT_ADMIN) ||
      roles.includes(ROLE.DRIVING.DAT_APPROVER) ||
      roles.includes(ROLE.DRIVING.ADMIN) ||
      roles.includes(ROLE.ADMIN)
    );
  } catch (_) {
    return false;
  }
}

// Tên người đăng nhập hiện tại (để hiển thị ngay "người duyệt" sau khi bấm).
function currentUserName() {
  try {
    const user = JSON.parse(localStorage.getItem('user-info') || 'null');
    return user?.name || user?.email || null;
  } catch (_) {
    return null;
  }
}

// Từ kết luận DAT (BE tính) + tuỳ chọn onlyPass/force → suy ra "kết quả sẽ
// đồng bộ" và HÀNH ĐỘNG dự kiến của 1 HV. Khớp đúng logic backend batch để
// preview không lệch với thực tế khi bấm chạy.
function studentSyncPlan(s, onlyPass, force) {
  const conc = s.conclusion; // 'pass' | 'fail' | 'no-session' | 'error' | undefined
  const isPass = conc === 'pass';
  const resultLabel =
    conc === 'no-session' ? 'Chưa có phiên'
      : conc === 'error' ? 'Lỗi tính kết luận'
        : isPass ? 'Đã đủ DAT' : 'Chưa đủ DAT';
  let action = 'sync';
  let reason = '';
  if (conc === 'no-session' || conc === 'error' || !conc) {
    action = 'skip'; reason = resultLabel;
  } else if (onlyPass && !isPass) {
    action = 'skip'; reason = 'Chưa đủ DAT';
  } else {
    const result = isPass ? 'pass' : 'fail';
    if (!force && s.datResultValue === result && s.datResultSyncedAt) {
      action = 'skip'; reason = 'Đã đồng bộ';
    }
  }
  return { isPass, conc, resultLabel, action, reason };
}

const LOI_NHAN_BO_QUA =
  'Hàng đợi Kiểm lần 1 / Kiểm lần 2 trả về danh sách riêng, không lọc theo tên hay hạng xe. Chọn "Tất cả" để tìm theo tên.';

function StudentPageV2() {
  const allowApprove = canApproveDatResult();
  const allowCheck = canCheckDat();
  const PAGE_SIZE = 20; // phân trang phía server: 20 học viên/trang
  // Filter theo hạng khoá học (prefix courseShortName). Mặc định chỉ hiện
  // BSS/BTĐ/C1 — ẩn A/A1 (yêu cầu 2026-07-21). Bỏ tick hết = hiện tất cả.
  const HANG_OPTIONS = ['BSS', 'BTĐ', 'C1', 'B2', 'B11', 'C', 'A', 'A1'];
  const DEFAULT_HANG = ['BSS', 'BTĐ', 'C1'];
  // Khởi tạo mọi filter TỪ URL để có deeplink (chia sẻ link ra đúng trạng thái).
  const params = new URLSearchParams(window.location.search);
  const initialPage = Math.max(1, parseInt(params.get('page') || '1', 10) || 1);
  const [name, setName] = useState(params.get('name') || '');
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [drivingClass, setDrivingClass] = useState(params.get('drivingClass') || '');
  // hang lưu nguyên trạng thái tick để deeplink khôi phục đúng; vắng param → mặc định.
  const [hangFilter, setHangFilter] = useState(
    params.has('hang') ? (params.get('hang') || '').split(',').filter(Boolean) : DEFAULT_HANG
  );
  // Mặc định chỉ hiện HV đã có phiên học gần nhất; bỏ tick → hiện cả HV chưa có phiên.
  const [onlyHasSession, setOnlyHasSession] = useState(params.get('hasSession') !== '0');
  // Hàng đợi đang xem. Gộp hai nút "Kiểm lần 1/2" và checkbox "Chờ phê duyệt"
  // về một cụm chọn-một: cả ba vốn đã loại trừ nhau, chỉ là trước đây nằm ở ba
  // dạng điều khiển khác nhau nên không nhìn ra.
  //
  // Vẫn đọc `?status=submitted` của link cũ — người dùng đã lưu và gửi cho nhau
  // những link đó, làm hỏng chúng là mất dữ liệu của người khác.
  const [hangDoi, setHangDoi] = useState(() =>
    docHangDoiTuUrl(
      params.get('queue') || (params.get('status') === 'submitted' ? HANG_DOI.CHO_DUYET : ''),
      allowApprove,
    ),
  );
  const truyVan = truyVanCuaHangDoi(hangDoi);
  const onlySubmitted = truyVan.onlySubmitted;
  // Hàng đợi kiểm không nhận tên / hạng xe — khoá hai ô đó lại.
  const boQuaTen = hangDoiBoQuaTen(hangDoi);
  // Bản "chốt" của tên (debounce) — query + URL bám theo bản này để gõ phím
  // không gọi API/đổi URL liên tục.
  const [debouncedName, setDebouncedName] = useState(params.get('name') || '');
  const [page, setPage] = useState(initialPage);
  const [total, setTotal] = useState(0);
  // paged=true: đang ở luồng tra cứu chính (có phân trang); false: luồng đặc
  // biệt (Tính tổng / Kiểm lần 1-2 / Chờ phê duyệt) trả về danh sách đầy đủ.
  const [paged, setPaged] = useState(true);
  // Chọn đủ 8 hạng cũng coi như không lọc.
  const hangParam =
    hangFilter.length > 0 && hangFilter.length < HANG_OPTIONS.length
      ? hangFilter.join(',')
      : '';
  const isDefaultHang =
    hangFilter.length === DEFAULT_HANG.length && DEFAULT_HANG.every((h) => hangFilter.includes(h));
  const toggleHang = (h) => {
    setPage(1);
    setHangFilter((prev) =>
      prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h]
    );
  };
  // Refs cho valueGetter STT (colDefs tạo 1 lần, cần đọc page/paged hiện tại).
  const pageRef = useRef(initialPage);
  pageRef.current = page;
  const pagedRef = useRef(true);
  pagedRef.current = paged;
  // Modal xác nhận đồng bộ học viên. `syncResult` giữ kết quả BE trả về
  // sau khi chạy (scanned/upserts...) để hiển thị ngay trong modal.
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  // Modal cập nhật kết quả DAT lên GetFly (chỉ DAT_APPROVER/admin). `approveRow` giữ
  // học viên đang xử lý; `approveSummary` là kết luận DAT do BE tính.
  const [approveRow, setApproveRow] = useState(null);
  const [approveSummary, setApproveSummary] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false); // đang tải summary
  const [approving, setApproving] = useState(false); // đang ghi GetFly
  const [approveError, setApproveError] = useState(null);
  const [approvedResult, setApprovedResult] = useState(null); // kết quả sau khi ghi

  // ── Gửi phê duyệt (người check gửi → approver duyệt/trả về) ──────────
  const [submittingCode, setSubmittingCode] = useState(null); // đang gửi phê duyệt
  const [rejectRow, setRejectRow] = useState(null);           // HV đang trả về
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // ── Đồng bộ HÀNG LOẠT theo khoá lên GetFly ──────────────────────────
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchCourseCode, setBatchCourseCode] = useState('');
  const [batchStudents, setBatchStudents] = useState(null); // null=chưa tải; [] hoặc [...]
  const [batchLoadingList, setBatchLoadingList] = useState(false);
  const [batchOnlyPass, setBatchOnlyPass] = useState(true);   // chỉ HV "Đã đủ DAT"
  const [batchForce, setBatchForce] = useState(false);        // ghi đè HV đã đồng bộ
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchDone, setBatchDone] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ processed: 0, total: 0 });
  const [batchResults, setBatchResults] = useState([]);       // [{registrationCode,name,status,message}]
  const batchCancelRef = useRef(false);
  const CHUNK_SIZE = 4;        // số HV mỗi request (đủ nhỏ để không timeout)
  const CHUNK_DELAY_MS = 800;  // giãn cách giữa các chunk (chống rate-limit)
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const openBatchModal = () => {
    setBatchStudents(null);
    setBatchResults([]);
    setBatchDone(false);
    setBatchProgress({ processed: 0, total: 0 });
    batchCancelRef.current = false;
    setShowBatchModal(true);
  };

  const loadBatchStudents = async () => {
    const code = batchCourseCode.trim();
    if (!code) { toast.warn('Nhập mã khoá học'); return; }
    setBatchLoadingList(true);
    setBatchStudents(null);
    setBatchResults([]);
    setBatchDone(false);
    try {
      const { data } = await datV2.getCourseStudents(code, { withConclusion: true });
      setBatchStudents(data?.students || []);
      if (!data?.students?.length) toast.info('Khoá này không có học viên nào');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Không tải được danh sách học viên');
      setBatchStudents([]);
    } finally {
      setBatchLoadingList(false);
    }
  };

  const runBatchSync = async () => {
    // Chỉ gửi HV DỰ KIẾN đồng bộ (bỏ qua HV chưa đủ/đã đồng bộ) → ít chunk hơn.
    const codes = (batchStudents || [])
      .filter((s) => studentSyncPlan(s, batchOnlyPass, batchForce).action === 'sync')
      .map((s) => s.registrationCode);
    if (!codes.length) { toast.info('Không có học viên nào cần đồng bộ theo tuỳ chọn hiện tại.'); return; }
    setBatchRunning(true);
    setBatchDone(false);
    setBatchResults([]);
    batchCancelRef.current = false;
    setBatchProgress({ processed: 0, total: codes.length });
    const acc = [];
    try {
      for (let i = 0; i < codes.length; i += CHUNK_SIZE) {
        if (batchCancelRef.current) break;
        const slice = codes.slice(i, i + CHUNK_SIZE);
        try {
          const { data } = await datV2.approveDatResultBatch(slice, {
            onlyPass: batchOnlyPass, force: batchForce,
          });
          acc.push(...(data?.results || []));
        } catch (err) {
          const msg = err?.response?.data?.message || 'Lỗi đồng bộ chunk';
          slice.forEach((registrationCode) => acc.push({ registrationCode, name: registrationCode, status: 'failed', message: msg }));
        }
        setBatchResults([...acc]);
        setBatchProgress({ processed: Math.min(i + CHUNK_SIZE, codes.length), total: codes.length });
        if (i + CHUNK_SIZE < codes.length && !batchCancelRef.current) await sleep(CHUNK_DELAY_MS);
      }
      setBatchDone(true);
      const synced = acc.filter((r) => r.status === 'synced').length;
      const failed = acc.filter((r) => r.status === 'failed').length;
      if (batchCancelRef.current) toast.info(`Đã dừng. Ghi ${synced}, lỗi ${failed}.`);
      else toast.success(`Xong: ghi ${synced}, bỏ qua ${acc.filter((r) => r.status === 'skipped').length}, lỗi ${failed}.`);
    } finally {
      setBatchRunning(false);
    }
  };

  const batchCounts = batchResults.reduce(
    (a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; },
    { synced: 0, skipped: 0, failed: 0 },
  );
  // Dự kiến (tính lại khi đổi tuỳ chọn) — để preview trong modal.
  const batchPlans = (batchStudents || []).map((s) => ({ s, plan: studentSyncPlan(s, batchOnlyPass, batchForce) }));
  const willSyncCount = batchPlans.filter((x) => x.plan.action === 'sync').length;
  const willSkipCount = batchPlans.length - willSyncCount;
  const [colDefs] = useState([
    {
      headerName: 'STT',
      valueGetter: (params) => {
        if (!params.node) return '';
        // Số thứ tự toàn cục theo trang (chỉ khi phân trang), ngược lại tính cục bộ.
        const base = pagedRef.current ? (pageRef.current - 1) * PAGE_SIZE : 0;
        return base + params.node.rowIndex + 1;
      },
      width: 80,
      pinned: 'left', // Cố định cột STT bên trái
    },
    {
      field: 'Chi tiết',
      headerName: 'Phiên học',
      cellRenderer: (params) => {
        const onClick = () => {
          window.open(
            `${window.location.origin}/admin/session?name=${params.data['registrationCode']}`,
            '_blank'
          );
        };
        return (
          <Button
            size='sm'
            variant='primary'
            onClick={onClick}
            className='w-100'
          >
            Xem
          </Button>
        );
      },
      width: 100,
    },
    { field: 'name', headerName: 'Họ và tên', flex: 1, minWidth: 300, cellRenderer: (p) => <CopyableText value={p.value} /> },
    {
      field: 'totalDistance',
      headerName: 'Tổng QĐ',
      flex: 1,
      minWidth: 150,
      cellRenderer: OQuangDuong,
      // Giữ valueFormatter để bản xuất CSV/Excel vẫn ra chuỗi như cũ — AG Grid
      // dùng nó khi xuất, chứ không dùng cellRenderer.
      valueFormatter: (params) => (params.value ? `${catHaiChuSo(params.value)} km` : ''),
    },
    {
      field: 'firstChecked',
      headerName: 'Kiểm lần 1',
      flex: 1,
      minWidth: 100,
      editable: true,
      valueGetter: (params) => {
        return params.data?.firstChecked === true
          ? true
          : params.data?.firstChecked === false
          ? false
          : null;
      },
      cellRenderer: 'agCheckboxCellRenderer',
    },
    {
      field: 'secondChecked',
      headerName: 'Kiểm lần 2',
      flex: 1,
      minWidth: 100,
      editable: true,
      valueGetter: (params) => {
        return params.data?.secondChecked === true
          ? true
          : params.data?.secondChecked === false
          ? false
          : null;
      },
      cellRenderer: 'agCheckboxCellRenderer',
    },
    {
      field: 'latest_start_time',
      headerName: 'Phiên học gần nhất',
      // Giờ phiên + mã phiên copy được (yêu cầu 2026-07-21).
      cellRenderer: (params) => {
        if (!params.value) return '';
        const time = new Date(params.value).toLocaleString('vi-VN', {
          timeZone: 'Asia/Ho_Chi_Minh',
        });
        const sid = params.data?.latest_session_id;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
            <span style={{ flex: '0 0 auto' }}>{time}</span>
            {sid ? (
              // Mã phiên rút gọn bằng ellipsis cho vừa ô — nút copy vẫn
              // sao chép mã ĐẦY ĐỦ.
              <span style={{ display: 'inline-flex', minWidth: 0, maxWidth: 150 }}>
                <CopyableText value={sid} />
              </span>
            ) : null}
          </span>
        );
      },
      flex: 1,
      minWidth: 320,
    },
    { field: 'courseShortName', headerName: 'Khoá học', flex: 1, minWidth: 80 },
    { field: 'registrationCode', headerName: 'Mã học viên', minWidth: 150, flex: 1, cellRenderer: (p) => <CopyableText value={p.value} /> },
    { field: 'dob', headerName: 'Ngày sinh', minWidth: 150, flex: 1 },
    {
      field: 'idCardNumber',
      headerName: 'Số CMND/CCCD/HC',
      valueFormatter: (params) => {
        return params.value ? params.value.toString() : '';
      },
      minWidth: 150,
      flex: 1,
    },
  ]);
  const [rowData, setRowData] = useState([]);

  const normalizeName = (raw) =>
    (raw || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[đĐ]/g, (m) => (m === 'đ' ? 'd' : 'D'));

  // Query TRA CỨU CHÍNH (server-side pagination 20/trang). Không bắt buộc nhập
  // tên: bỏ trống thì tải toàn bộ theo các filter (hạng khoá / có phiên).
  const buildQuery = (pageOverride) => ({
    page: pageOverride ?? page,
    limit: PAGE_SIZE,
    ...(debouncedName.trim() ? { name: normalizeName(debouncedName) } : {}),
    ...(drivingClass.trim() ? { drivingClass: drivingClass.trim() } : {}),
    ...(hangParam ? { hang: hangParam } : {}),
    ...(onlyHasSession ? { hasSession: true } : {}),
    ...(onlySubmitted ? { approvalStatus: 'submitted' } : {}),
  });

  const fetchStudents = async (q) => {
    setLoading(true);
    setPaged(true);
    try {
      const res = await datV2.searchStudent(q);
      setRowData(res.data || []);
      setTotal(res.pagination?.total ?? (res.data?.length || 0));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Chạy đúng truy vấn của hàng đợi đang đứng.
   *
   * Mọi chỗ nạp bảng đều phải đi qua đây. Trước đây `handleSearch` gọi thẳng
   * `fetchStudents`, nên bấm "Tra cứu" khi đang ở tab "Kiểm lần 2" sẽ nạp TOÀN
   * BỘ học viên — kể cả người đã kiểm và khoá cũ — trong khi tab vẫn sáng
   * "Kiểm lần 2". Không có gì báo sai, và người dùng tin đó là hàng đợi.
   */
  const chayTruyVanHangDoi = (pageOverride) => {
    if (truyVan.kieu === 'kiem-lan-1') return searchFirstCheckStudent();
    if (truyVan.kieu === 'kiem-lan-2') return searchSecondCheckStudent();
    return fetchStudents(buildQuery(pageOverride));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setDebouncedName(name);
    chayTruyVanHangDoi(1);
  };

  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const goToPage = (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    if (next !== page) setPage(next);
  };

  const calcTotalDistance = async () => {
    setCalculating(true);
    setPaged(false);
    setTotal(0);
    try {
      const { data } = await datV2.calcTotalDistance();
      setRowData(data);
      toast.success('Tính tổng thành công');
    } catch (error) {
      console.error(error);
    } finally {
      setCalculating(false);
    }
  }

  const searchFirstCheckStudent = async () => {
    setLoading(true);
    setPaged(false);
    setTotal(0);
    try {
      const { data } = await datV2.searchFirstCheckStudent(hangParam ? { hang: hangParam } : {});
      setRowData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const searchSecondCheckStudent = async () => {
    setLoading(true);
    setPaged(false);
    setTotal(0);
    try {
      const { data } = await datV2.searchSecondCheckStudent(hangParam ? { hang: hangParam } : {});
      setRowData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  // Mở modal xác nhận trước khi đồng bộ.
  const openSyncModal = () => {
    setSyncResult(null);
    setShowSyncModal(true);
  };

  // Chạy đồng bộ sau khi người dùng xác nhận trong modal.
  const confirmSyncStudent = async () => {
    setSyncing(true);
    try {
      const { data } = await datV2.syncStudent();
      setSyncResult(data || {});
      toast.success('Đồng bộ học viên thành công');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Đồng bộ học viên thất bại');
    } finally {
      setSyncing(false);
    }
  }

  // Cập nhật 1 dòng trong bảng tại chỗ (không phải search lại cả trang).
  const patchRow = (registrationCode, patch) => {
    setRowData((prev) => prev.map((r) => (
      r.registrationCode === registrationCode ? { ...r, ...patch } : r
    )));
  };

  // Người CHECK gửi 1 HV cho approver phê duyệt (sau khi check xong hết phiên).
  const handleSubmitForApproval = async (row) => {
    setSubmittingCode(row.registrationCode);
    try {
      const { data } = await datV2.submitForApproval(row.registrationCode);
      patchRow(row.registrationCode, {
        datApprovalStatus: 'submitted',
        datSubmittedByName: data?.datSubmittedByName || null,
        datSubmittedAt: data?.datSubmittedAt || new Date().toISOString(),
        datRejectedReason: null,
      });
      toast.success('Đã gửi phê duyệt');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Gửi phê duyệt thất bại', { autoClose: 6000 });
    } finally {
      setSubmittingCode(null);
    }
  };

  // Approver trả về 1 HV đã gửi kèm lý do.
  const confirmReject = async () => {
    if (!rejectRow?.registrationCode || !rejectReason.trim()) return;
    setRejecting(true);
    try {
      await datV2.rejectApproval(rejectRow.registrationCode, rejectReason.trim());
      patchRow(rejectRow.registrationCode, {
        datApprovalStatus: 'rejected',
        datRejectedReason: rejectReason.trim(),
      });
      toast.success('Đã trả về cho người check');
      setRejectRow(null);
      setRejectReason('');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Trả về thất bại', { autoClose: 6000 });
    } finally {
      setRejecting(false);
    }
  };

  // Ô "Phê duyệt": badge trạng thái + nút gửi (người check) / trả về (approver).
  const renderApprovalCell = (row) => {
    const status = row?.datApprovalStatus || 'none';
    // KHÔNG chặn theo row.allChecked (giá trị CACHE, chỉ cập nhật khi bấm
    // "Tính tổng" → hay bị cũ). BE kiểm tra allChecked TƯƠI từ phiên khi gửi,
    // nên cứ hiện nút cho HV chưa gửi/bị trả về; gửi sai thì BE báo lỗi rõ.
    const canSubmit = allowCheck && (status === 'none' || status === 'rejected');
    return (
      <div className='d-flex flex-column gap-1 py-1' style={{ lineHeight: 1.2 }}>
        {status === 'submitted' && (
          <Badge bg='info' text='dark'>Chờ duyệt{row?.datSubmittedByName ? ` · ${row.datSubmittedByName}` : ''}</Badge>
        )}
        {status === 'approved' && (
          <>
            <Badge bg='success'>Đã duyệt{row?.datResultSyncedByName ? ` · ${row.datResultSyncedByName}` : ''}</Badge>
            {row?.datResultSyncedAt && (
              <span className='text-muted' style={{ fontSize: '0.72rem' }}>
                {new Date(row.datResultSyncedAt).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' })}
              </span>
            )}
          </>
        )}
        {status === 'rejected' && (
          <Badge bg='danger' title={row?.datRejectedReason || ''}>Bị trả về</Badge>
        )}
        {(status === 'none' || !status) && <Badge bg='secondary'>Chưa gửi</Badge>}
        {status === 'rejected' && row?.datRejectedReason && (
          <span className='text-danger' style={{ fontSize: '0.72rem' }}>Lý do: {row.datRejectedReason}</span>
        )}
        {canSubmit && (
          <Button
            size='sm' variant='outline-primary' className='w-100 py-0'
            disabled={submittingCode === row.registrationCode}
            onClick={() => handleSubmitForApproval(row)}
            title='Gửi cho admin phê duyệt & đồng bộ'
          >
            {submittingCode === row.registrationCode ? 'Đang gửi…' : (status === 'rejected' ? 'Gửi lại' : 'Gửi phê duyệt')}
          </Button>
        )}
        {allowApprove && status === 'submitted' && (
          <Button
            size='sm' variant='outline-danger' className='w-100 py-0'
            onClick={() => { setRejectRow(row); setRejectReason(''); }}
            title='Trả về cho người check kèm lý do'
          >
            Trả về
          </Button>
        )}
      </div>
    );
  };

  // Mở modal cập nhật kết quả DAT lên GetFly cho 1 học viên: tải kết luận
  // DAT (BE tính) để hiển thị trước khi ghi.
  const openApprove = async (row) => {
    setApproveRow(row);
    setApproveSummary(null);
    setApproveError(null);
    setApprovedResult(null);
    setApproveLoading(true);
    try {
      const { data } = await datV2.getStudentSummary(row.registrationCode);
      setApproveSummary(data || null);
    } catch (err) {
      setApproveError(err?.response?.data?.message || 'Không tải được kết luận DAT');
    } finally {
      setApproveLoading(false);
    }
  };

  // Ghi kết quả lên GetFly sau khi xác nhận.
  const confirmApprove = async () => {
    if (!approveRow?.registrationCode) return;
    const isPass = Boolean(approveSummary?.valid && approveSummary?.allChecked);
    setApproving(true);
    try {
      const res = await datV2.approveDatResult(approveRow.registrationCode, isPass ? 'pass' : 'fail');
      setApprovedResult({ result: isPass ? 'pass' : 'fail', syncedAt: res?.data?.syncedAt || new Date().toISOString() });
      patchRow(approveRow.registrationCode, {
        datApprovalStatus: 'approved',
        datResultValue: isPass ? 'pass' : 'fail',
        datResultSyncedAt: res?.data?.syncedAt || new Date().toISOString(),
        datResultSyncedByName: currentUserName(),
      });
      toast.success('Đã cập nhật kết quả DAT lên GetFly');
    } catch (err) {
      const msg = err?.response?.data?.message || 'Cập nhật GetFly thất bại';
      setApproveError(msg);
      toast.error(msg, { autoClose: 8000 });
    } finally {
      setApproving(false);
    }
  };

  const onCellValueChanged = (params) => {
    const { data, api } = params;

    if (data.firstChecked === false && data.secondChecked === true) {
      data.firstChecked = true;
      api.applyTransaction({ update: [data] });
    }

    const body = {
      'registrationCode': data['registrationCode'],
      firstChecked: data.firstChecked,
      secondChecked: data.secondChecked
    };

    datV2.updateStudent(body)
      .then(() => {
        toast('Cập nhật thành công', { type: 'success' });
      })
      .catch(() => {
        toast('Cập nhật thất bại', { type: 'error' });
      });
  }

  useEffect(() => {
    document.title = 'Tra cứu học viên DAT';
  }, []);

  // Gõ tên → chốt sau 600ms (tránh gọi API/đổi URL mỗi phím).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(name), 600);
    return () => clearTimeout(t);
  }, [name]);

  // Tra cứu chính: chạy khi tên đã chốt / đổi hạng xe / hạng khoá / có-phiên /
  // trang. Bỏ trống tên vẫn tải (toàn bộ theo filter). 20 item/trang.
  useEffect(() => {
    // Ba hàng đợi, ba truy vấn. Trước đây hai cái phải bấm nút mới chạy, nên
    // đổi bộ lọc khi đang ở "Kiểm lần 1" thì bảng vẫn giữ kết quả cũ mà không
    // báo gì. Giờ đổi gì cũng chạy lại đúng truy vấn của hàng đợi đang đứng.
    chayTruyVanHangDoi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedName, drivingClass, hangParam, onlyHasSession, hangDoi, page]);

  // Đồng bộ URL để có DEEPLINK (chia sẻ link giữ nguyên filter + trang).
  useEffect(() => {
    const p = new URLSearchParams();
    if (debouncedName.trim()) p.set('name', debouncedName.trim());
    if (drivingClass.trim()) p.set('drivingClass', drivingClass.trim());
    if (!isDefaultHang) p.set('hang', hangFilter.join(',')); // mặc định → bỏ cho gọn
    if (!onlyHasSession) p.set('hasSession', '0');            // mặc định ON → chỉ ghi khi tắt
    if (hangDoi !== HANG_DOI.TAT_CA) p.set('queue', hangDoi);
    if (page > 1) p.set('page', String(page));
    const qs = p.toString();
    window.history.replaceState(null, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
  }, [debouncedName, drivingClass, hangFilter, isDefaultHang, onlyHasSession, hangDoi, page]);

  return (
    <>
    <V2PageLayout
      title='Tra cứu học viên DAT'
      filters={
        <>
          {/* Hai hàng đợi kiểm gọi endpoint riêng, chỉ nhận hạng khoá — ô tên
              và ô hạng xe không tới được chúng. Khoá lại kèm lời giải thích;
              để mở là hứa một bộ lọc không chạy. */}
          <Form.Control
            type='text'
            size='sm'
            style={{ width: 260 }}
            placeholder={boQuaTen ? 'Không lọc theo tên ở hàng đợi này' : 'Tên hoặc mã học viên'}
            aria-label='Tên hoặc mã học viên'
            title={boQuaTen ? LOI_NHAN_BO_QUA : undefined}
            disabled={boQuaTen}
            value={name}
            onChange={(e) => { setPage(1); setName(e.target.value); }}
          />
          <Form.Control
            type='text'
            size='sm'
            style={{ width: 120 }}
            placeholder='Hạng xe'
            aria-label='Hạng xe'
            title={boQuaTen ? LOI_NHAN_BO_QUA : undefined}
            disabled={boQuaTen}
            value={drivingClass}
            onChange={(e) => { setPage(1); setDrivingClass(e.target.value); }}
          />
          <Button
            size='sm'
            onClick={handleSearch}
            variant='primary'
            disabled={loading}
            title={boQuaTen ? 'Tải lại hàng đợi đang xem' : undefined}
          >
            {boQuaTen ? 'Tải lại' : 'Tra cứu'}
          </Button>
          <span className='vr mx-1' />
          {/* Hạng khoá nằm cạnh ô tìm kiếm vì nó cũng là bộ lọc — trước đây nó
              bị đẩy xuống một hàng riêng nên trông như một nhóm khác. */}
          <span className='text-muted small'>Hạng khoá</span>
          {HANG_OPTIONS.map((h) => (
            <Form.Check
              key={h}
              inline
              className='mb-0 small'
              type='checkbox'
              id={`hang-${h}`}
              label={h}
              checked={hangFilter.includes(h)}
              onChange={() => toggleHang(h)}
            />
          ))}
          <Form.Check
            inline
            className='mb-0 small'
            type='checkbox'
            id='only-has-session'
            label='Chỉ HV đã có phiên'
            checked={onlyHasSession}
            onChange={(e) => { setPage(1); setOnlyHasSession(e.target.checked); }}
            title='Bỏ tick để hiện cả học viên chưa có phiên học nào'
          />
        </>
      }
      actions={
        <Dropdown align='end'>
          <Dropdown.Toggle size='sm' variant='outline-secondary' id='thao-tac-hoc-vien'>
            Thao tác
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {/* Chỉ ba mục này thực sự GHI dữ liệu. Tách khỏi bộ lọc để không ai
                bấm nhầm khi đang định lọc. */}
            <Dropdown.Item onClick={openSyncModal} disabled={syncing}>
              Đồng bộ học viên
            </Dropdown.Item>
            <Dropdown.Item onClick={calcTotalDistance} disabled={calculating}>
              Tính tổng quãng đường
            </Dropdown.Item>
            {allowApprove && (
              <Dropdown.Item onClick={openBatchModal}>Đồng bộ cả khoá</Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      }
      queues={
        <CumHangDoi
          giaTri={hangDoi}
          onChon={(h) => { setPage(1); setHangDoi(h); }}
          allowApprove={allowApprove}
          disabled={loading}
        />
      }
      footer={
        paged && total > 0 ? (
          <div className='d-flex align-items-center gap-2'>
            <Button size='sm' variant='outline-secondary' disabled={loading || page <= 1} onClick={() => goToPage(1)}>« Đầu</Button>
            <Button size='sm' variant='outline-secondary' disabled={loading || page <= 1} onClick={() => goToPage(page - 1)}>‹ Trước</Button>
            <span className='text-muted small dat-km'>
              Trang <strong>{page}</strong> / {totalPages} · Tổng <strong>{total}</strong> HV
            </span>
            <Button size='sm' variant='outline-secondary' disabled={loading || page >= totalPages} onClick={() => goToPage(page + 1)}>Sau ›</Button>
            <Button size='sm' variant='outline-secondary' disabled={loading || page >= totalPages} onClick={() => goToPage(totalPages)}>Cuối »</Button>
          </div>
        ) : null
      }
    >
      {loading ? (
        <p className='text-center'>Đang tìm kiếm...</p>
      ) : rowData.length ? (
        // `height: 100%` chứ KHÔNG phải `flex: 1 1 auto`: vùng bảng của
        // V2PageLayout là một block, không phải flex container, nên thuộc tính
        // flex ở đây bị bỏ qua và AG Grid sập còn 2px — bảng biến mất trong khi
        // thanh phân trang vẫn hiện đủ số. Dòng chữ vẫn nằm trong DOM, nên mọi
        // phép kiểm đếm `innerText` đều báo xanh; phải đo chiều cao thật.
        <div style={{ height: '100%' }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={[
              ...colDefs,
              {
                headerName: 'Phê duyệt',
                pinned: 'right',
                width: 200,
                autoHeight: true,
                cellRenderer: (params) => renderApprovalCell(params.data),
              },
              ...(allowApprove
                ? [
                    {
                      headerName: 'Kết quả GetFly',
                      pinned: 'right',
                      width: 160,
                      cellRenderer: (params) => (
                        <Button
                          size='sm'
                          variant='outline-success'
                          className='w-100'
                          onClick={() => openApprove(params.data)}
                          title='Cập nhật kết quả DAT lên GetFly'
                        >
                          Cập nhật GetFly
                        </Button>
                      ),
                    },
                  ]
                : []),
            ]}
            onCellValueChanged={onCellValueChanged}
          />
        </div>
      ) : (
        <p className='text-center'>Không có dữ liệu</p>
      )}

    </V2PageLayout>

      <Modal show={showSyncModal} onHide={() => !syncing && setShowSyncModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Đồng bộ học viên DAT</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!syncResult ? (
            <>
              <p className='mb-2'>
                Thao tác này sẽ <strong>đồng bộ danh sách học viên</strong> từ hệ thống
                đào tạo (uniapp Driving) sang cơ sở dữ liệu DAT của trung tâm.
              </p>
              <ul className='mb-2'>
                <li>Thêm mới học viên chưa có, cập nhật thông tin học viên đã có.</li>
                <li>Không xoá học viên; không ảnh hưởng dữ liệu phiên học.</li>
                <li>Có thể mất vài giây tuỳ số lượng học viên.</li>
              </ul>
              <p className='mb-0'>Bạn có chắc chắn muốn tiếp tục?</p>
            </>
          ) : (
            <div>
              <p className='text-success fw-bold mb-2'>Đồng bộ hoàn tất.</p>
              <dl className='row mb-0'>
                <dt className='col-6'>Số học viên quét</dt>
                <dd className='col-6'>{syncResult.scanned ?? '—'}</dd>
                <dt className='col-6'>Thêm/cập nhật (upsert)</dt>
                <dd className='col-6'>{syncResult.upserts ?? '—'}</dd>
                {syncResult.inserted != null && (
                  <>
                    <dt className='col-6'>Thêm mới</dt>
                    <dd className='col-6'>{syncResult.inserted}</dd>
                  </>
                )}
                {syncResult.updated != null && (
                  <>
                    <dt className='col-6'>Cập nhật</dt>
                    <dd className='col-6'>{syncResult.updated}</dd>
                  </>
                )}
                {syncResult.skipped != null && (
                  <>
                    <dt className='col-6'>Bỏ qua</dt>
                    <dd className='col-6'>{syncResult.skipped}</dd>
                  </>
                )}
              </dl>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!syncResult ? (
            <>
              <Button
                variant='secondary'
                onClick={() => setShowSyncModal(false)}
                disabled={syncing}
              >
                Huỷ
              </Button>
              <Button variant='primary' onClick={confirmSyncStudent} disabled={syncing}>
                {syncing ? (
                  <>
                    <Spinner animation='border' size='sm' /> Đang đồng bộ...
                  </>
                ) : (
                  'Đồng bộ ngay'
                )}
              </Button>
            </>
          ) : (
            <Button variant='secondary' onClick={() => setShowSyncModal(false)}>
              Đóng
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Đồng bộ HÀNG LOẠT kết quả DAT theo khoá lên GetFly */}
      <Modal
        show={showBatchModal}
        onHide={() => !batchRunning && setShowBatchModal(false)}
        centered
        size='lg'
      >
        <Modal.Header closeButton={!batchRunning}>
          <Modal.Title>Đồng bộ kết quả DAT cả khoá lên GetFly</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className='g-2 align-items-end mb-2'>
            <Col xs={7}>
              <Form.Label className='mb-1 small fw-bold'>Mã khoá học</Form.Label>
              <Form.Control
                type='text'
                placeholder='VD: BTĐ-K35 hoặc mã khoá CSDL'
                value={batchCourseCode}
                onChange={(e) => setBatchCourseCode(e.target.value)}
                disabled={batchRunning}
                onKeyDown={(e) => { if (e.key === 'Enter' && !batchRunning) loadBatchStudents(); }}
              />
            </Col>
            <Col xs={5}>
              <Button
                variant='outline-primary'
                className='w-100'
                onClick={loadBatchStudents}
                disabled={batchRunning || batchLoadingList}
              >
                {batchLoadingList ? (<><Spinner animation='border' size='sm' /> Đang tải...</>) : 'Tải danh sách'}
              </Button>
            </Col>
          </Row>

          <div className='d-flex gap-3 mb-2'>
            <Form.Check
              type='checkbox'
              id='batch-onlypass'
              label={`Chỉ HV "Đã đủ DAT"`}
              checked={batchOnlyPass}
              onChange={(e) => setBatchOnlyPass(e.target.checked)}
              disabled={batchRunning}
            />
            <Form.Check
              type='checkbox'
              id='batch-force'
              label='Ghi đè HV đã đồng bộ'
              checked={batchForce}
              onChange={(e) => setBatchForce(e.target.checked)}
              disabled={batchRunning}
            />
          </div>

          {batchStudents != null && (
            <div className='mb-2'>
              <p className='mb-1 small'>
                Khoá có <strong>{batchStudents.length}</strong> học viên —{' '}
                <Badge bg='success' className='me-1'>Sẽ đồng bộ {willSyncCount}</Badge>
                <Badge bg='secondary'>Bỏ qua {willSkipCount}</Badge>
              </p>

              {batchStudents.length > 0 && !batchRunning && !batchDone && (
                <>
                  <div style={{ maxHeight: 300, overflowY: 'auto' }} className='border rounded'>
                    <Table hover size='sm' className='mb-0 small align-middle'>
                      <thead className='table-light' style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          <th style={{ width: 34 }}>#</th>
                          <th>Học viên</th>
                          <th>Kết quả sẽ đồng bộ</th>
                          <th>Dự kiến</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchPlans.map(({ s, plan }, idx) => (
                          <tr key={s.registrationCode}>
                            <td className='text-muted'>{idx + 1}</td>
                            <td>
                              <div>{s.name || s.registrationCode}</div>
                              <div className='text-muted' style={{ fontSize: '0.72rem' }}>
                                {s.registrationCode}{s.drivingType ? ` · ${s.drivingType}` : ''}
                              </div>
                            </td>
                            <td>
                              {plan.conc === 'pass' && <Badge bg='success'>Đã đủ DAT</Badge>}
                              {plan.conc === 'fail' && <Badge bg='warning' text='dark'>Chưa đủ DAT</Badge>}
                              {(plan.conc === 'no-session' || plan.conc === 'error' || !plan.conc) && (
                                <Badge bg='light' text='dark'>{plan.resultLabel}</Badge>
                              )}
                              {s.datResultSyncedAt && (
                                <div className='text-muted' style={{ fontSize: '0.72rem' }}>
                                  đã ghi: {s.datResultValue === 'pass' ? 'Đã đủ' : 'Chưa đủ'}
                                </div>
                              )}
                            </td>
                            <td>
                              {plan.action === 'sync' ? (
                                <span className='text-success fw-semibold'>Sẽ ghi</span>
                              ) : (
                                <span className='text-muted'>Bỏ qua ({plan.reason})</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <p className='text-muted mt-1 mb-0' style={{ fontSize: '0.75rem' }}>
                    Chỉ {willSyncCount} HV "Sẽ ghi" được gửi lên GetFly (mỗi lượt {CHUNK_SIZE} HV, có giãn cách chống rate-limit).
                    Đổi tuỳ chọn ở trên sẽ tính lại danh sách.
                  </p>
                </>
              )}
            </div>
          )}

          {(batchRunning || batchDone) && (
            <>
              <div className='progress mb-2' style={{ height: 20 }}>
                <div
                  className={`progress-bar ${batchDone ? 'bg-success' : 'progress-bar-striped progress-bar-animated'}`}
                  role='progressbar'
                  style={{ width: `${batchProgress.total ? Math.round((batchProgress.processed / batchProgress.total) * 100) : 0}%` }}
                >
                  {batchProgress.processed}/{batchProgress.total}
                </div>
              </div>
              <p className='mb-2 small'>
                <Badge bg='success' className='me-1'>Ghi {batchCounts.synced}</Badge>
                <Badge bg='secondary' className='me-1'>Bỏ qua {batchCounts.skipped}</Badge>
                <Badge bg='danger'>Lỗi {batchCounts.failed}</Badge>
              </p>
              {batchResults.length > 0 && (
                <div style={{ maxHeight: 240, overflowY: 'auto' }} className='border rounded small'>
                  {batchResults.map((r, idx) => (
                    <div
                      key={`${r.registrationCode}-${idx}`}
                      className='d-flex justify-content-between px-2 py-1 border-bottom'
                    >
                      <span className='text-truncate me-2'>{r.name || r.registrationCode}</span>
                      <span className='flex-shrink-0'>
                        {r.status === 'synced' && <Badge bg='success'>đã ghi</Badge>}
                        {r.status === 'skipped' && <Badge bg='secondary'>{r.message || 'bỏ qua'}</Badge>}
                        {r.status === 'failed' && <Badge bg='danger' title={r.message}>lỗi</Badge>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {batchRunning ? (
            <Button variant='warning' onClick={() => { batchCancelRef.current = true; }}>
              Dừng
            </Button>
          ) : (
            <>
              <Button variant='secondary' onClick={() => setShowBatchModal(false)}>
                Đóng
              </Button>
              {batchStudents?.length > 0 && !batchDone && (
                <Button variant='success' onClick={runBatchSync} disabled={willSyncCount === 0}>
                  Bắt đầu đồng bộ ({willSyncCount} HV)
                </Button>
              )}
              {batchDone && (
                <Button variant='outline-success' onClick={() => { setBatchDone(false); setBatchResults([]); }}>
                  Đồng bộ lại
                </Button>
              )}
            </>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!approveRow}
        onHide={() => !approving && setApproveRow(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cập nhật kết quả DAT lên GetFly</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-2'>
            Học viên: <strong>{approveRow?.name}</strong>{' '}
            <span className='text-muted'>({approveRow?.registrationCode})</span>
          </p>

          {approveLoading && (
            <div className='text-center py-2'>
              <Spinner animation='border' size='sm' /> Đang tải kết luận DAT...
            </div>
          )}

          {!approveLoading && approveError && !approvedResult && (
            <p className='text-danger mb-0'>{approveError}</p>
          )}

          {!approveLoading && approveSummary && !approvedResult && (
            <>
              <p className='mb-2'>
                Kết luận DAT:{' '}
                {approveSummary.valid && approveSummary.allChecked ? (
                  <span className='text-success fw-bold'>Đã đủ DAT</span>
                ) : (
                  <span className='text-danger fw-bold'>
                    {approveSummary.message
                      ? approveSummary.message
                      : approveSummary.allChecked
                      ? 'Chưa đủ điều kiện'
                      : 'Chưa kiểm đủ phiên'}
                  </span>
                )}
              </p>
              <p className='mb-0 text-muted small'>
                Giá trị sẽ ghi vào field <strong>KIỂM TRA DAT</strong> trên GetFly:{' '}
                <Badge bg={approveSummary.valid && approveSummary.allChecked ? 'success' : 'secondary'}>
                  {approveSummary.valid && approveSummary.allChecked ? 'Đã đủ DAT' : 'Chưa đủ DAT'}
                </Badge>
              </p>

              {approveSummary.getflyFields?.length > 0 && (
                <div className='mt-3'>
                  <p className='mb-1 small fw-bold'>Số liệu DAT sẽ đồng bộ sang GetFly:</p>
                  <ul className='list-unstyled mb-1 small'>
                    {approveSummary.getflyFields.map((f) => (
                      <li
                        key={f.slug}
                        className='d-flex justify-content-between align-items-center py-1 border-bottom'
                      >
                        <span className={f.configured ? '' : 'text-muted'}>
                          {f.name}: <strong>{f.value ?? '—'}</strong>
                        </span>
                        {f.configured ? (
                          <Badge bg='success'>sẽ đồng bộ</Badge>
                        ) : (
                          <Badge bg='light' text='dark'>chưa cấu hình</Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className='mb-0 text-muted' style={{ fontSize: '0.75rem' }}>
                    Field “chưa cấu hình” sẽ KHÔNG được cập nhật (cấu hình field_id ở tab Integration).
                  </p>
                </div>
              )}
            </>
          )}

          {approvedResult && (
            <>
              <p className='text-success fw-bold mb-0'>
                Đã cập nhật lên GetFly (
                {approvedResult.result === 'pass' ? 'Đã đủ DAT' : 'Chưa đủ DAT'}) lúc{' '}
                {new Date(approvedResult.syncedAt).toLocaleString('vi-VN', {
                  timeZone: 'Asia/Ho_Chi_Minh',
                })}
                .
              </p>
              {approvedResult.syncedFields?.length > 0 && (
                <p className='text-muted small mb-0 mt-1'>
                  Đã đồng bộ thêm {approvedResult.syncedFields.length} số liệu:{' '}
                  {approvedResult.syncedFields.join(', ')}.
                </p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {approvedResult ? (
            <Button variant='secondary' onClick={() => setApproveRow(null)}>
              Đóng
            </Button>
          ) : (
            <>
              <Button variant='secondary' onClick={() => setApproveRow(null)} disabled={approving}>
                Huỷ
              </Button>
              <Button
                variant='success'
                onClick={confirmApprove}
                disabled={approving || approveLoading || !approveSummary}
              >
                {approving ? (
                  <>
                    <Spinner animation='border' size='sm' /> Đang cập nhật...
                  </>
                ) : (
                  'Cập nhật lên GetFly'
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Trả về phê duyệt (approver) — nhập lý do để người check sửa & gửi lại */}
      <Modal show={!!rejectRow} onHide={() => !rejecting && setRejectRow(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Trả về phê duyệt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-2'>
            Học viên: <strong>{rejectRow?.name}</strong>{' '}
            <span className='text-muted'>({rejectRow?.registrationCode})</span>
          </p>
          {rejectRow?.datSubmittedByName && (
            <p className='mb-2 small text-muted'>Người gửi: {rejectRow.datSubmittedByName}</p>
          )}
          <Form.Label className='mb-1 small fw-bold'>Lý do trả về <span className='text-danger'>*</span></Form.Label>
          <Form.Control
            as='textarea'
            rows={3}
            placeholder='VD: phiên ngày… bị sai, cần kiểm tra lại…'
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={rejecting}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setRejectRow(null)} disabled={rejecting}>
            Huỷ
          </Button>
          <Button variant='danger' onClick={confirmReject} disabled={rejecting || !rejectReason.trim()}>
            {rejecting ? (<><Spinner animation='border' size='sm' /> Đang trả về...</>) : 'Trả về'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default StudentPageV2;
